/**
 * Recovery for "stale tab across a deploy" chunk failures.
 *
 * The app is code-split, so every route is a hashed chunk (`index-b093a206.js`,
 * `ReadAloudButton-20760bbe.css`, ...). A browser that loaded `index.html`
 * before a deploy still holds references to the PREVIOUS build's hashes, and
 * those files are gone from the CDN the moment the new build goes live. The
 * next lazy route the student opens then fails to load.
 *
 * On Vercel this fails in a particularly confusing way: the SPA rewrite in
 * vercel.json (`/(.*)` -> `/index.html`) catches the missing `/assets/...`
 * request and returns `index.html` with HTTP 200 and `content-type: text/html`.
 * The file "exists" as far as status codes go — which is why this looks like a
 * transient network blip rather than a missing file — but the browser refuses
 * to apply HTML as a stylesheet or execute it as a module. Vite's preload
 * helper then rejects with `Unable to preload CSS for ...`, React.lazy rejects,
 * and the error boundary shows the student a dead end.
 *
 * The only real remedy is to fetch the new `index.html` — i.e. reload. Retrying
 * the same URLs cannot help: those files are permanently gone. The reload is
 * therefore deliberate, and guarded so a genuinely broken deploy degrades to
 * the error screen instead of reloading forever.
 *
 * Reloading is safe at the point these errors happen: the route chunk fails
 * BEFORE its page renders, so there is no in-progress work to lose, and
 * `history.state` (which carries React Router's location state, e.g. a test
 * taker's student details and resume session) survives a reload.
 */

const RELOAD_STAMP_KEY = "p3m_chunk_reload_at";
const RELOAD_COUNT_KEY = "p3m_chunk_reload_count";
/** Two reloads inside this window means reloading is not fixing it. */
const RELOAD_WINDOW_MS = 30_000;
const MAX_RELOADS = 2;

/** Set once a reload is under way, so repeat reports don't re-spend the budget. */
let reloadStarted = false;

const CHUNK_ERROR_PATTERNS = [
    // Vite's preload helper, for a CSS dep of a lazy chunk.
    /unable to preload css/i,
    // Dynamic import of a missing/mis-typed JS chunk.
    /failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /importing a module script failed/i,
    // Webpack-style wording, and Safari/Firefox MIME refusals.
    /loading chunk \S+ failed/i,
    /expected a javascript(?:-or-wasm)? module script/i,
];

const readStorage = (key: string): string | null => {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        // Private mode / storage disabled.
        return null;
    }
};

const writeStorage = (key: string, value: string): void => {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        // Non-fatal: without storage we simply lose the loop guard.
    }
};

/** True for the "the build moved under me" family of load failures. */
export const isChunkLoadError = (error: unknown): boolean => {
    if (!error) return false;
    const text =
        typeof error === "string"
            ? error
            : error instanceof Error
              ? `${error.name} ${error.message}`
              : String((error as any)?.message ?? error);
    if (!text) return false;
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
};

/**
 * Reload once to pick up the current `index.html`.
 *
 * Returns true when a reload was started (the caller should keep showing its
 * loading state and NOT render an error), false when the budget is spent and
 * the caller should surface a real error instead.
 */
export const recoverFromChunkError = (): boolean => {
    if (typeof window === "undefined") return false;
    // React can surface the same error more than once (getDerivedStateFromError
    // plus a listener, or a double render), and the reload is not instantaneous.
    // Answer consistently instead of spending the budget several times over.
    if (reloadStarted) return true;

    const now = Date.now();
    const lastAt = Number(readStorage(RELOAD_STAMP_KEY) || 0);
    const count = Number(readStorage(RELOAD_COUNT_KEY) || 0);

    // Outside the window, treat this as a fresh incident.
    const withinWindow = lastAt > 0 && now - lastAt < RELOAD_WINDOW_MS;
    const attempts = withinWindow ? count : 0;

    if (attempts >= MAX_RELOADS) {
        // Reloading is not resolving it — let the error boundary take over.
        return false;
    }

    reloadStarted = true;
    writeStorage(RELOAD_STAMP_KEY, String(now));
    writeStorage(RELOAD_COUNT_KEY, String(attempts + 1));

    // `true` forces a fresh document rather than the bfcache/memory copy that
    // still points at the old asset hashes.
    window.location.reload();
    return true;
};

/**
 * Wire up the automatic recovery. Call once, as early as possible — before any
 * lazy route can be requested.
 */
export const installChunkErrorRecovery = (): void => {
    if (typeof window === "undefined") return;

    // Vite fires this when a lazy chunk's CSS/JS preload link fails. Handling it
    // here is what stops the student ever reaching the error screen.
    window.addEventListener("vite:preloadError", (event) => {
        if (recoverFromChunkError()) {
            // Suppress Vite's rethrow; the page is already reloading.
            event.preventDefault();
        }
    });

    // A dynamic import that rejects outside React's render path (e.g. a
    // prefetch) surfaces here rather than at the error boundary.
    window.addEventListener("unhandledrejection", (event) => {
        if (!isChunkLoadError(event.reason)) return;
        if (recoverFromChunkError()) {
            event.preventDefault();
        }
    });
};
